import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdImportExport } from "react-icons/md";

import { IDE_TYPE } from "../../../../constants/common";
import { useSnack } from "../../../../hooks/useSnack";
import studentServices from "../../../../services/studentServices";
import teacherServices from "../../../../services/teacherServices";
import Button from "../../../Button";
import CodeEditor from "../../editors/webIDE/WebCodeEditor";
import CodeSolutionsHeader from "../../headings/teacher/web/CodeSolutionsHeader";
import PlaygroundHeader from "../../headings/teacher/web/PlaygroundHeader";
import ShareCodeHeader from "../../headings/teacher/web/ShareCodeHeader";
import SubmissionsHeader from "../../headings/teacher/web/SubmissionsHeader";

const TeacherWebIDEWrapper = ({ type = "playground", onSubmit, ...rest }) => {
  const { t } = useTranslation();
  const [html, setHtml] = useState("");
  const [css, setCss] = useState("");
  const [js, setJs] = useState("");
  const [codeData, setCodeData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [projectID, setProjectID] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [projectName, setProjectName] = useState("");

  const { showSnack } = useSnack();

  const saveProject = async (payload, isAutoSave) => {
    if (!isAutoSave) setIsSaving(true);
    try {
      let response;
      if (projectData?.id || projectID) {
        // Edit existing project
        response = await teacherServices.editTeacherProjects(projectData?.id || projectID, payload);
      } else {
        // Create new project
        response = await teacherServices.createTeacherProjects(payload);
        setProjectID(response?.project?.id);
      }
      if (isAutoSave) return;

      showSnack(t("saved_successfully"), "success");
    } catch (error) {
      showSnack(error, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProject = async (newProjectName, isAutoSave) => {
    const payload = {
      html: html || "",
      css: css || "",
      js: js || "",
      type: IDE_TYPE.WEB,
      name: newProjectName || projectData?.name,
    };
    setProjectName(newProjectName);
    saveProject(payload, isAutoSave);
  };

  const handleGetPlaygroundProject = (payload) => {
    setCodeData(payload.code);
    setProjectData(payload.project);
  };

  const handleExportCode = async () => {
    setIsExporting(true);
    try {
      const response = await studentServices.exportProject(projectID);

      let fileName = `${projectName || projectData?.name}.zip`;

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnack(t("exported_successfully"), "success");
    } catch (error) {
      console.error("Error exporting project:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreateNewProject = () => {
    setCodeData(null);
    setProjectData(null);
    setProjectID(null);
    setHtml("");
    setCss("");
    setJs("");
  };

  const renderHeader = () => {
    switch (type) {
      case "playground":
        return (
          <PlaygroundHeader
            onGetPlaygroundProject={handleGetPlaygroundProject}
            onCreateNewProject={handleCreateNewProject}
            onSaveProject={handleSaveProject}
            initialProjectName={projectData?.name}
            html={html}
            css={css}
            js={js}
            isSaving={isSaving}
          />
        );
      case "solutions":
        return <CodeSolutionsHeader rest={rest} />;
      case "share":
        return <ShareCodeHeader />;
      case "submissions":
        return <SubmissionsHeader onSubmit={onSubmit} rest={rest} />;
      default:
        return null;
    }
  };

  return (
    <div className="relative rounded-[12px] bg-blue-100 px-4 py-4 shadow-lg dark:bg-dark-surface">
      {renderHeader()}
      {type !== "submissions" && type !== "solutions" && (
        <Button
          className="absolute right-[55px] top-[60px] z-50 h-10 w-28"
          leftIcon={<MdImportExport size={26} />}
          loading={isExporting}
          label={t("export")}
          onClick={handleExportCode}
          disabled={isExporting || !projectID}
        />
      )}
      <CodeEditor
        isTeacher
        html={html}
        css={css}
        js={js}
        setHtml={setHtml}
        setCss={setCss}
        setJs={setJs}
        initialCode={rest?.codeData?.code || codeData}
      />
    </div>
  );
};

export default TeacherWebIDEWrapper;
